import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import {
  setRendererFormState,
  setSelectValidError,
} from "../../../store/slices/commonSlice";
import { renderElement } from "../../../components/common/FormRendererV2/Renderer";
import { Button, Form } from "react-bootstrap";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "../index.scss";
import {
  getParamsFromURL,
  setParamsInURL,
  useIsMobile,
  getCodeFromUrl,
} from "../../../utils/helper";
import { AG_CODE } from "../../../utils/commonConstant";
import {
  ordersDetails,
  orderSummary,
  paymentSummary,
  kyc,
} from "../../../utils/router/constant";
import { updateForm } from "../../../service/proposal/proposalService";
import { setData } from "../../../store/slices/proposal/proposalSlice";
import api from "../../../utils/interface";
import StepFormTabs from "../../../components/common/stepFormTabs";
import { getDependentAttributeValues } from "../../../service/productVariant/filterService";
import CraftProposalForm from "./craftProposalForm";
import useProposalFormData, {
  denormalizeProposalRecord,
} from "../hook/useProposalFormData";
import useProposalWizard from "../hook/useProposalWizard";
import ProposalNavButtons from "./proposalNavButtons";

function MembersDetails() {
  const dispatch = useDispatch();
  const nav = useNavigate();
  const location = useLocation();
  const agCode = getCodeFromUrl();
  const agDetails = useSelector((state) => state.common.agDetails);
  // chem orders skip the proposal, except profile_enabled false (MSCDA),
  // where the proposal is filled after the profile and before payment
  const skipProposal =
    (agCode?.toLowerCase() === AG_CODE || agCode?.toLowerCase() === "chem") &&
    agDetails?.profile_enabled !== false;

  const orderDetail = useSelector((state) => state.buyNow.data);
  // getViewMasterData's `renderer` flag. 1 = drive the form through
  // @craft-form/renderer; anything else falls back to the legacy
  // FormRendererV2 + Redux-formState renderer below.
  const isNewRenderer = useSelector(
    (state) => state.common.rendererVersion === 1,
  );
  const [orderId, setOrderId] = useState(null);
  const [entityid, setEntityId] = useState(null);

  // --- legacy (FormRendererV2) renderer state ---
  const formState = useSelector((state) => state.common.formRendererState);
  const ProposalFieldsJSON = useSelector(
    (state) => state.buyNow.ProposalFieldsJSON,
  );
  const ProposalLayoutJSON = useSelector(
    (state) => state.buyNow.ProposalLayoutJSON || [],
  );
  const readProposalForm = useSelector((state) => state.buyNow.proposalJson);
  const [index, setIndex] = useState(0);
  const [validationErrors, setValidationErrors] = useState([]);
  const isMobile = useIsMobile();

  // --- new (craft-form) renderer state ---
  const proposalValues = useProposalFormData();
  // Passed to denormalizeProposalRecord on the submit paths so it can work out
  // which selects hold a bare year — same wiring as the admin panel's
  // useSubmitProposalForm.
  const proposalFields = useSelector(
    (state) => state.buyNow.ProposalFieldsJSON,
  );

  // Owned here rather than inside CraftProposalForm so "Save Draft" and the
  // navigation buttons below can read and validate the form's live values —
  // @craft-form/renderer takes this instance via its `methods` prop.
  // `values` (not `defaultValues`) so the form re-seeds once readPrposalData
  // resolves, which happens after this first renders.
  const craftFormMethods = useForm({
    values: proposalValues,
    mode: "onChange",
  });

  useEffect(() => {
    const fetchCode = async () => {
      await getParamsFromURL(location).then((params) => {
        if (params) {
          setOrderId(params["orderId"]);
          setEntityId(params["entityid"]);
        } else {
          console.error("No parameters found");
        }
      });
    };
    fetchCode();
  }, [location]);

  useEffect(() => {
    if (
      orderId &&
      orderDetail &&
      skipProposal &&
      (orderDetail.status === "PAYMENT_PENDING" ||
        orderDetail.status === "PAYMENT_FAILED")
    ) {
      const paramObject = { entityid, orderId };
      setParamsInURL(location, paramObject).then((params) => {
        nav(`${paymentSummary}?${params}`, { replace: true });
      });
    }
  }, [orderId, orderDetail, entityid, skipProposal, nav, location]);

  // Legacy renderer only: doctor_category derives from dn_highqual through
  // formRendererState, which the new renderer never touches.
  useEffect(() => {
    if (isNewRenderer) return;
    const qualValue = formState?.dn_highqual || readProposalForm?.dn_highqual;
    if (qualValue && entityid) {
      getDependentAttributeValues(entityid, "qualification", qualValue).then(
        (response) => {
          if (response) {
            dispatch(
              setRendererFormState({
                key: "doctor_category",
                value:
                  response.attribute_lov_label ||
                  response.default_values ||
                  response.doctor_category ||
                  response[0]?.default_values,
              }),
            );
          }
        },
      );
    }
  }, [
    isNewRenderer,
    formState?.dn_highqual,
    readProposalForm?.dn_highqual,
    entityid,
  ]);

  // ===== legacy renderer handlers =====
  const handleFieldChange = (fieldName, value, isValid, tabIndex) => {
    if (!isValid && !validationErrors.includes(tabIndex)) {
      setValidationErrors((prev) => [...prev, tabIndex]);
      dispatch(setSelectValidError(true));
    } else if (isValid) {
      setValidationErrors((prev) =>
        prev.filter((errorIndex) => errorIndex !== tabIndex),
      );
      if (validationErrors.length === 1) {
        dispatch(setSelectValidError(false));
      }
    }
  };

  const handleLegacyPrevious = async () => {
    if (index === 0) {
      const paramObject = { entityid, orderId };
      const params = await setParamsInURL(location, paramObject);
      orderDetail?.product_type_name === "UMBRELLA"
        ? nav(-1)
        : nav(`${orderSummary}?${params}`);
    } else if (index > 0) setIndex(index - 1);
  };

  const handleLegacySaveDraft = async (e) => {
    e.preventDefault();
    const response = await updateForm(
      formState,
      orderId,
      "ODR",
      false,
      orderDetail.status,
      0,
    );
    if (response) {
      toast.success("Saved draft successfully");
    }
  };

  const handleLegacySubmit = async (e) => {
    e.preventDefault();
    if (validationErrors.length === 0) {
      if (index < ProposalLayoutJSON.length - 1) {
        setIndex(index + 1);
        updateForm(formState, orderId, "ODR", null, orderDetail.status, 0);
      } else {
        const paramObject = { orderId };
        const params = await setParamsInURL(location, paramObject);

        try {
          const response = await updateForm(
            formState,
            orderId,
            "ODR",
            null,
            orderDetail.status,
            1,
          );

          if (response != undefined || response != null) {
            let updatedOrderDetail = orderDetail;
            try {
              const fdDetails = new FormData();
              fdDetails.append("entityid", orderId);
              fdDetails.append("entitytype", "ODR");
              const detailsResponse = await api.post(`/entity/read`, fdDetails);
              if (detailsResponse && detailsResponse.data) {
                updatedOrderDetail = detailsResponse.data;
                dispatch(setData(updatedOrderDetail));
              }
            } catch (err) {
              console.error("Failed to re-fetch order details:", err);
            }

            console.log("updatedOrderDetail after legacy submit:", updatedOrderDetail);

            if (
              (updatedOrderDetail.product_type_name === "UMBRELLA" &&
                updatedOrderDetail.redirect_url) ||
              skipProposal
            ) {
              nav(`${ordersDetails}?${params}`);
            } else if ((updatedOrderDetail.show_kyc || updatedOrderDetail.showKyc) && !updatedOrderDetail.is_kyc && updatedOrderDetail.kyc_status !== "Completed") {
              const memberId = updatedOrderDetail.billing_member_id || updatedOrderDetail.member_id || "";
              nav(`/?orderId=${orderId}&memberId=${memberId}&openKyc=true`);
            } else {
              nav(`${paymentSummary}?${params}`);
            }
          }
        } catch (error) {
          console.error("API call failed", error);
        }
      }
    } else {
      dispatch(setSelectValidError(true));
    }
  };

  // ===== new (craft-form) renderer handlers =====
  // Fires once, on Finish — handleSubmit has already validated every step by
  // the time this runs. Mirrors the routing the previous wizard did on Finish.
  const handleSave = async (values) => {
    const params = await setParamsInURL(location, { orderId });

    // proposal_filled is 1 ONLY when every required field is filled. Finish
    // has normally validated already, but this is the single place that sends
    // 1, so re-check here rather than trust every caller. If anything is still
    // missing, keep the data as a draft (0) and stay on the form.
    const allRequiredFilled = await craftFormMethods.trigger();
    if (!allRequiredFilled) {
      await updateForm(
        denormalizeProposalRecord(craftFormMethods.getValues(), proposalFields),
        orderId,
        "ODR",
        false,
        orderDetail.status,
        0,
      );
      toast.error("Please fill all required fields before submitting the proposal.");
      return;
    }

    try {
      const response = await updateForm(
        denormalizeProposalRecord(values, proposalFields),
        orderId,
        "ODR",
        false,
        orderDetail.status,
        1,
      );

      if (response) {
        let updatedOrderDetail = orderDetail;
        try {
          const fdDetails = new FormData();
          fdDetails.append("entityid", orderId);
          fdDetails.append("entitytype", "ODR");
          const detailsResponse = await api.post(`/entity/read`, fdDetails);
          if (detailsResponse && detailsResponse.data) {
            updatedOrderDetail = detailsResponse.data;
            dispatch(setData(updatedOrderDetail));
          }
        } catch (err) {
          console.error("Failed to re-fetch order details:", err);
        }

        console.log("updatedOrderDetail after craft-form handleSave:", updatedOrderDetail);

        if (
          (updatedOrderDetail.product_type_name === "UMBRELLA" &&
            updatedOrderDetail.redirect_url) ||
          skipProposal
        ) {
          nav(`${ordersDetails}?${params}`);
        } else if ((updatedOrderDetail.show_kyc || updatedOrderDetail.showKyc) && !updatedOrderDetail.is_kyc && updatedOrderDetail.kyc_status !== "Completed") {
          const memberId = updatedOrderDetail.billing_member_id || updatedOrderDetail.member_id || "";
          nav(`/?orderId=${orderId}&memberId=${memberId}&openKyc=true`);
        } else {
          nav(`${paymentSummary}?${params}`);
        }
      }
    } catch (error) {
      console.error("API call failed", error);
    }
  };

  const { activeStep, setActiveStep } = useProposalWizard(
    craftFormMethods,
    handleSave,
  );

  // proposal_filled: 0 — a draft is explicitly not a completed proposal. The
  // save is never blocked by validation (a draft is allowed to be incomplete),
  // but trigger() runs afterwards so unfilled required fields are still
  // flagged as required to the user.
  const handleSaveDraft = async () => {
    const response = await updateForm(
      denormalizeProposalRecord(craftFormMethods.getValues(), proposalFields),
      orderId,
      "ODR",
      false,
      orderDetail.status,
      0,
    );
    if (response) {
      toast.success("Saved draft successfully");
    }
    craftFormMethods.trigger();
  };

  // Where "Previous" goes when there is no earlier step to fall back to.
  const exitForm = async () => {
    const params = await setParamsInURL(location, { entityid, orderId });
    if (orderDetail?.product_type_name === "UMBRELLA") {
      nav(-1);
    } else {
      nav(`${orderSummary}?${params}`);
    }
  };

  // ===== legacy renderer =====
  if (!isNewRenderer) {
    return (
      <div>
        <div className="proposal-header">
          <h2 className="order-title" onClick={() => nav(-1)}>
            {orderDetail.name} ({orderDetail.code})
          </h2>
          <Button
            className="btn-outline"
            onClick={(e) => handleLegacySaveDraft(e)}
          >
            Save Draft
          </Button>
        </div>
        <div className="proposal-container">
          <Form onSubmit={handleLegacySubmit}>
            {isMobile ? (
              <div className="mobile-step-tabs">
                <StepFormTabs
                  steps={ProposalLayoutJSON}
                  currentStep={index}
                  direction="horizontal"
                />
              </div>
            ) : (
              <div className="proposal-tab-wrapper">
                {ProposalLayoutJSON.map((element, ind) => (
                  <div
                    key={element.elementid || ind}
                    className={`proposal-active ${index === ind ? "active-tab" : "inactive-tab"
                      }`}
                    style={{
                      pointerEvents: ind <= index ? "auto" : "none",
                      opacity: ind <= index ? 1 : 0.5,
                    }}
                  >
                    <div className="step-container">
                      <div>
                        <span
                          className={`step-indicator ${index >= ind ? "active-step-indicator" : ""
                            }`}
                        >
                          {ind + 1}
                        </span>
                        {ind < ProposalLayoutJSON.length - 1 && (
                          <div
                            className={`step-line ${index < index ? "active-step-line" : ""
                              }`}
                          ></div>
                        )}
                      </div>
                    </div>
                    {renderElement(
                      element,
                      ProposalFieldsJSON,
                      formState,
                      (fieldName, value, isValid) =>
                        handleFieldChange(fieldName, value, isValid, ind),
                    )}
                  </div>
                ))}
              </div>
            )}

            <div className="form-content">
              {ProposalLayoutJSON[index]?.child?.map((child) =>
                renderElement(
                  child,
                  ProposalFieldsJSON,
                  formState,
                  (fieldName, value, isValid) =>
                    handleFieldChange(fieldName, value, isValid, index),
                ),
              )}
            </div>

            <div className="navigation-buttons">
              <Button variant="primary previous" onClick={handleLegacyPrevious}>
                Previous
              </Button>
              {index === ProposalLayoutJSON.length - 1 ? (
                <Button variant="primary next" type="submit" color="success">
                  Finish
                </Button>
              ) : (
                <Button variant="primary next" type="submit" color="primary">
                  Next
                </Button>
              )}
            </div>
          </Form>
        </div>
      </div>
    );
  }

  // ===== new (craft-form) renderer =====
  return (
    <div>
      <div className="proposal-header">
        <h2 className="order-title" onClick={() => nav(-1)}>
          {orderDetail.name} ({orderDetail.code})
        </h2>
        <Button className="btn-outline" onClick={handleSaveDraft}>
          Save Draft
        </Button>
      </div>

      <div className="proposal-container">
        {/* The renderer draws the stepper and the fields; its own nav buttons
            are suppressed so the buttons stay this project's own. They render
            as children — inside CraftFormProvider, which is the only place
            useFormNavigation() resolves. */}
        <CraftProposalForm
          methods={craftFormMethods}
          activeStep={activeStep}
          setActiveStep={setActiveStep}
          // Always "create" mode for the end user: update mode applies each
          // field's updateOverrides, which strips `required` — so once a
          // draft was saved (proposal_complete true) the required markers
          // and checks disappeared. The end user must always see them.
          isEditing={false}
          onSave={handleSave}
        >
          <ProposalNavButtons onExit={exitForm} onJumpToStep={setActiveStep} />
        </CraftProposalForm>
      </div>
    </div>
  );
}

export default MembersDetails;
